import { ReactElement, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import cogoToast from 'cogo-toast';
import AuthContext from '../contexts/AuthContext';
import FavoritosContext, { FavoritosContextValue } from '../contexts/FavoritosContext';
import { getMenuFavoritos, setMenuFavoritos } from '../api/MenuFavoritosApi';
import { esFavoritable } from '../utils/menuGroups';

const MAX_FAVORITOS = 30;

interface FavoritosStoreProps {
    children?: ReactNode;
}

/**
 * Favoritos del menú lateral, guardados en el backend por RUT. La estrella
 * responde al instante (actualización optimista); si el guardado falla se
 * vuelve a la última lista confirmada por el servidor.
 */
const FavoritosStore = ({ children }: FavoritosStoreProps): ReactElement => {
    const { userRut, userRoles } = useContext(AuthContext);
    const [favoritos, setFavoritos] = useState<string[]>([]);
    const confirmados = useRef<string[]>([]);
    // Solo la respuesta del último guardado puede pisar el estado: si el
    // usuario marca dos estrellas seguidas, las respuestas pueden llegar
    // en desorden.
    const ultimoGuardado = useRef(0);

    const esCliente = userRoles.includes('CLIENTE');
    // Lista vigente para toggleFavorito, sin efectos dentro de un updater de setState.
    const favoritosRef = useRef<string[]>([]);
    favoritosRef.current = favoritos;

    useEffect(() => {
        if (!userRut || esCliente) return;
        let vigente = true;
        getMenuFavoritos()
            .then(({ data }) => {
                if (!vigente || !Array.isArray(data)) return;
                confirmados.current = data;
                setFavoritos(data);
            })
            .catch((error) => console.error('[Favoritos] No se pudieron cargar:', error));
        return () => { vigente = false; };
    }, [userRut, esCliente]);

    const guardar = useCallback((nuevos: string[]): void => {
        const id = ++ultimoGuardado.current;
        favoritosRef.current = nuevos;
        setFavoritos(nuevos);
        setMenuFavoritos(nuevos)
            .then(({ data }) => {
                confirmados.current = data;
                if (id === ultimoGuardado.current) setFavoritos(data);
            })
            .catch((error) => {
                if (id !== ultimoGuardado.current) return;
                setFavoritos(confirmados.current);
                cogoToast.error(error.response?.data?.message || 'No se pudo guardar el favorito. Intente nuevamente.');
            });
    }, []);

    const toggleFavorito = useCallback((href: string): void => {
        if (!esFavoritable(href)) return;
        const actuales = favoritosRef.current;
        if (actuales.includes(href)) {
            guardar(actuales.filter((f) => f !== href));
        } else if (actuales.length >= MAX_FAVORITOS) {
            cogoToast.warn(`Puede tener hasta ${MAX_FAVORITOS} favoritos. Quite alguno para agregar otro.`);
        } else {
            guardar([...actuales, href]);
        }
    }, [guardar]);

    const value: FavoritosContextValue = {
        favoritos,
        esFavorito: (href: string) => favoritos.includes(href),
        toggleFavorito,
    };

    return <FavoritosContext.Provider value={value}>{children}</FavoritosContext.Provider>;
};

export default FavoritosStore;
